import React, { useEffect, useMemo, useRef, useState } from 'react'
import api from '../../lib/api'
import Button from '../ui/Button'
import { Card, CardHeader } from '../ui/Card'

function formatRemaining(ms) {
  if (ms <= 0) return '00:00'
  const totalSeconds = Math.ceil(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export default function SupportVideoRoom({ room, user }) {
  const previewRef = useRef(null)
  const streamRef = useRef(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [cameraEnabled, setCameraEnabled] = useState(true)
  const [audioEnabled, setAudioEnabled] = useState(true)
  const [devicesReady, setDevicesReady] = useState(false)
  const [now, setNow] = useState(Date.now())

  const isLocalRoom = !user || String(room?.id || '').startsWith('local-') || String(room?.id || '').startsWith('local-copy-')

  const stopPreview = () => {
    const stream = streamRef.current
    if (stream) stream.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (previewRef.current) previewRef.current.srcObject = null
    setDevicesReady(false)
  }

  const loadSession = async ({ silent = false } = {}) => {
    if (!room?.id || isLocalRoom) return
    if (!silent) setLoading(true)
    try {
      const { data } = await api.get(`/support/rooms/${room.id}/video`)
      setSession(data.session || null)
      setError('')
    } catch (err) {
      if (!silent) setError(err?.response?.data?.error || 'Não foi possível consultar a videochamada.')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    setSession(null)
    setError('')
    stopPreview()
    loadSession()
    return () => stopPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.id])

  useEffect(() => {
    if (!session || session.status !== 'active') return undefined
    const timer = window.setInterval(() => {
      setNow(Date.now())
      loadSession({ silent: true })
    }, 10000)
    return () => window.clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status, session?.expiresAt])

  const remainingMs = useMemo(() => {
    if (!session?.expiresAt) return 0
    return Math.max(0, new Date(session.expiresAt).getTime() - now)
  }, [session?.expiresAt, now])

  const testDevices = async () => {
    setError('')
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Este navegador não disponibiliza câmera/microfone. Use HTTPS ou localhost e um navegador atualizado.')
      return
    }
    try {
      stopPreview()
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      streamRef.current = stream
      if (previewRef.current) previewRef.current.srcObject = stream
      setCameraEnabled(true)
      setAudioEnabled(true)
      setDevicesReady(true)
    } catch (err) {
      setError(`Não foi possível acessar câmera/microfone. Verifique as permissões do navegador. ${err?.message || ''}`.trim())
    }
  }

  const toggleTrack = (kind) => {
    const stream = streamRef.current
    if (!stream) return
    const tracks = kind === 'video' ? stream.getVideoTracks() : stream.getAudioTracks()
    const nextEnabled = kind === 'video' ? !cameraEnabled : !audioEnabled
    tracks.forEach((track) => { track.enabled = nextEnabled })
    if (kind === 'video') setCameraEnabled(nextEnabled)
    else setAudioEnabled(nextEnabled)
  }

  const startSession = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await api.post(`/support/rooms/${room.id}/video/start`)
      setSession(data.session)
      setNow(Date.now())
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível iniciar a videochamada.')
    } finally {
      setLoading(false)
    }
  }

  const endSession = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await api.post(`/support/rooms/${room.id}/video/end`)
      setSession(data.session)
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível encerrar a videochamada.')
    } finally {
      setLoading(false)
    }
  }

  if (isLocalRoom) {
    return (
      <Card>
        <CardHeader title="Vídeo on-line" subtitle="Câmera e áudio com profissional" />
        <div className="alert info">A videochamada exige login e conexão com a API para proteger o acesso à sala.</div>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader title="Vídeo on-line" subtitle="Teste câmera e microfone antes de entrar na sessão" />

      {error ? <div className="alert">{error}</div> : null}

      <div className="support-video-grid">
        <div className="support-video-preview-wrap">
          <video ref={previewRef} className="support-video-preview" autoPlay muted playsInline />
          {!devicesReady ? <div className="support-video-placeholder">Pré-visualização local da câmera</div> : null}
        </div>

        <div className="stack-sm">
          <p><strong>Privacidade:</strong> o teste é local no navegador. O Math não grava nem envia essa prévia ao servidor.</p>
          <div className="button-wrap">
            <Button variant="secondary" onClick={testDevices}>Testar câmera e microfone</Button>
            <Button variant="ghost" disabled={!devicesReady} onClick={() => toggleTrack('video')}>{cameraEnabled ? 'Desligar câmera' : 'Ligar câmera'}</Button>
            <Button variant="ghost" disabled={!devicesReady} onClick={() => toggleTrack('audio')}>{audioEnabled ? 'Desligar microfone' : 'Ligar microfone'}</Button>
          </div>
          {devicesReady ? <div className="alert info">Câmera e microfone liberados. Você pode entrar na sessão.</div> : null}
        </div>
      </div>

      <div className="support-video-session mt-4">
        {loading && !session ? <p>Consultando sessão...</p> : null}
        {session ? (
          <>
            <div className="meta-row">
              <span>Status: <strong>{session.status === 'active' ? 'Em andamento' : session.status === 'ended' ? 'Encerrada' : 'Aguardando início'}</strong></span>
              <span>Limite: <strong>{session.durationMinutes} min</strong></span>
              {session.status === 'active' ? <span>Tempo restante: <strong>{formatRemaining(remainingMs)}</strong></span> : null}
            </div>

            <div className="button-wrap mt-4">
              {session.canStart && session.status !== 'active' ? <Button onClick={startSession}>Iniciar videochamada</Button> : null}
              {session.canEnd && session.status === 'active' ? <Button variant="danger" onClick={endSession}>Encerrar videochamada</Button> : null}
            </div>

            {session.status === 'idle' && !session.canStart ? <div className="alert info mt-4">Aguardando o profissional iniciar a videochamada.</div> : null}
            {session.status === 'ended' ? <div className="alert info mt-4">A sessão foi encerrada. O profissional pode iniciar uma nova sessão se o atendimento ainda estiver aberto.</div> : null}

            {session.canJoin && session.meetingUrl ? (
              <div className="support-video-embed mt-4">
                <iframe
                  title={`Videochamada ${room.title || room.id}`}
                  src={session.meetingUrl}
                  allow="camera; microphone; fullscreen; display-capture; autoplay"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Card>
  )
}
